import type { Screen } from '../state/app.js';

// 화면별 도움말(일반 사용자 기준) — 상단 '?' 버튼의 반투명 레이어에 표시.
// Record<Screen, …> 이라 새 화면을 추가하면 도움말 누락이 컴파일 오류로 드러난다.
export interface ScreenHelp {
  intro: string;
  sections: Array<{ title: string; items: string[] }>;
  tip?: string;
}

export const SCREEN_HELP: Record<Screen, ScreenHelp> = {
  home: {
    intro: '공지사항, 오늘의 출퇴근, 내 연차, 사무실 근무 현황을 한 화면에서 확인하는 첫 화면입니다.',
    sections: [
      { title: '출퇴근·자리비움', items: [
        '출근 시 “출근하기”, 퇴근 시 “퇴근하기”를 누릅니다. 출근은 하루 1번만 기록됩니다.',
        '잘못 퇴근했다면 “퇴근 취소”로 근무 중 상태로 되돌릴 수 있습니다.',
        '잠시 자리를 비울 때는 “자리비움”에서 외근·회의·병원·개인 용무 중 사유를 고르거나 직접 입력합니다(오늘만 등록).',
      ] },
      { title: '공지·연차', items: [
        '공지를 누르면 상세 내용이 열립니다. 읽지 않은 공지는 점으로 표시됩니다.',
        '내 연차 카드에서 잔여·사용·승인대기 일수를 보고 “휴가 신청”으로 바로 신청할 수 있습니다.',
      ] },
      { title: '근무 현황·회의실', items: [
        '평면도의 좌석을 누르면 그 직원의 상태를, 우클릭하면 Teams 메시지 보내기를 할 수 있습니다.',
        '회의실을 누르면 날짜·시간·안건을 입력해 예약합니다(오늘 이후, 종료가 시작보다 늦어야 함).',
        '내 예약은 시작 전이면 “×”로 삭제, 진행 중이면 “사용완료”로 끝낼 수 있습니다.',
      ] },
    ],
    tip: '출장 중인 날에는 출퇴근이 정시로 자동 기록되고 상태가 ‘출장’으로 표시됩니다.',
  },
  board: {
    intro: '전 임직원의 입사일·근무일수·생일·연락처·주소를 보는 화면입니다. 오늘의 상태와 일정은 “근무현황”에서 확인합니다.',
    sections: [
      { title: '내 행에서 할 수 있는 일', items: [
        '“프로파일”: 학력·근무경력·자격증·업무경력을 입력·저장하고 PDF·Excel로 내려받습니다.',
        '“증명서”: 재직증명서·자격증 등을 분류를 골라 올리고, 내려받거나 삭제합니다(파일 1개 6MB 이하).',
      ] },
    ],
    tip: '이름·연락처·주소·생일 같은 기본 정보는 왼쪽 아래 내 이름(내 정보)에서 고칩니다.',
  },
  leave: {
    intro: '내 연차 현황을 확인하고 휴가를 신청·취소하는 화면입니다.',
    sections: [
      { title: '휴가 신청', items: [
        '“휴가 신청”에서 종류(연차·오전반차·오후반차·병가·경조·공가), 기간, 사유를 입력해 신청합니다.',
        '반차는 0.5일로 계산되며 하루만 고를 수 있습니다.',
        '신청 일수가 잔여 연차를 넘으면 신청할 수 없습니다.',
      ] },
      { title: '신청 내역', items: [
        '“내 휴가 신청 내역”에서 진행 상태를 확인합니다. 승인은 대표이사가 합니다.',
        '승인 전(상신·승인대기)에만 “신청 취소”가 가능합니다. 승인된 휴가 취소는 관리자에게 요청하세요.',
      ] },
    ],
  },
  trip: {
    intro: '내 출장을 등록·취소하고, 출장에서 쓴 경비를 청구하는 화면입니다.',
    sections: [
      { title: '출장 등록', items: [
        '“출장 등록”에서 기간, 출장 장소(필수), 내역(목적·내용)을 입력합니다.',
        '출장 기간의 평일은 정시 출퇴근이 자동으로 기록됩니다.',
        '일정이 바뀌면 “취소” 후 다시 등록하세요(취소는 바로 반영됩니다).',
      ] },
      { title: '출장 경비', items: [
        '각 출장의 “경비 청구”를 누르면 그 출장이 연결된 경비 청구서가 열립니다.',
        '이미 청구한 경비가 있으면 “경비 N건 · 금액”이 표시되고, 누르면 경비지출 화면으로 이동합니다.',
      ] },
    ],
  },
  overtime: {
    intro: '오늘 야근을 신청하고, 시작·종료 버튼으로 실제 야근 시간을 남기는 화면입니다.',
    sections: [
      { title: '신청', items: [
        '“야근 신청”에서 시작 예정(오후 6시 이후), 종료 예정, 업무 내용을 입력합니다. 자리비움처럼 당일 건만 신청할 수 있습니다.',
        '종료 예정이 시작보다 이른 시각이면 다음 날로 보며, 다음 날 오전 9시 전이어야 합니다.',
        '하루에 한 건만 신청할 수 있고, 시작 전이면 “취소”할 수 있습니다.',
      ] },
      { title: '시작·종료', items: [
        '위쪽 카드의 “야근 시작”을 누르면 실제 시작 시각이, “야근 종료”를 누르면 종료 시각이 기록됩니다.',
        '신청과 진행 상태는 근무현황에 ‘야근예정’·‘야근중’으로 표시되고, 개인별 이력은 근무통계분석에서 볼 수 있습니다.',
      ] },
    ],
  },
  weekly: {
    intro: '매주 회의 전에 지난주 한 일과 이번 주 계획을 공유하는 화면입니다. 기본 회의는 매주 월요일 10:00입니다.',
    sections: [
      { title: '작성하기(보고자)', items: [
        '목록에서 “다음 회의”를 열고 “내 칸으로”를 누르면 내 칸으로 이동합니다.',
        '“전주 진행 내용”과 “금주 진행 예정”을 작성하고 “저장”을 누릅니다. 굵게·밑줄·목록 서식을 쓸 수 있습니다.',
        '“전주 진행 예정”은 지난주에 쓴 ‘금주 진행 예정’이 자동으로 들어오며 수정할 수 없습니다.',
      ] },
      { title: '작성 기간', items: [
        '직전 회의 당일 오후 1시부터 회의 시작 전까지 작성·수정할 수 있습니다.',
        '회의가 시작되면 그 주 보고는 마감됩니다. 회의 일시가 바뀌면 기간도 함께 바뀝니다.',
      ] },
      { title: '업무진행/공지사항', items: [
        '화면 아래 업무진행·공지사항·참고용은 보고를 받는 분(피보고자)이 작성합니다.',
        '그 주에 새로 쓰지 않았다면 가장 최근 내용이 그대로 보입니다.',
      ] },
    ],
    tip: '작성 중에 목록으로 나가려 하면 저장하지 않은 내용이 있다고 알려줍니다.',
  },
  expense: {
    intro: '개인 카드·현금으로 쓴 비용(환급)과 법인카드 사용 내역을 영수증과 함께 청구하는 화면입니다. 결재와 지급 확인은 대표이사가 합니다.',
    sections: [
      { title: '청구서 작성', items: [
        '“+ 새 청구”에서 제목·관련 프로젝트를 쓰고, 사용 내역을 줄마다 입력합니다(사용일·분류·결제수단·사용처·내용·금액).',
        '한 청구서에 개인 지출과 법인카드 사용을 함께 적을 수 있고, 합계가 자동 계산됩니다.',
        '영수증 사진·PDF를 끌어다 놓거나 선택해 첨부합니다(휴대폰은 바로 촬영 가능, 1개 50MB 이하).',
        '“임시 저장”으로 나중에 이어 쓰고, 다 되면 “제출”합니다. 제출하면 대표이사에게 알림 메일이 갑니다.',
      ] },
      { title: '진행 상태', items: [
        '작성중 → 결재대기 → (개인 지출이 있으면) 지급대기 → 완료 순으로 진행됩니다.',
        '결재대기 중에는 “회수”해서 고칠 수 있고, 반려되면 반려 사유를 보고 고쳐서 다시 제출합니다.',
        '출장 경비는 출장 화면의 “경비 청구”로 시작하면 출장이 자동으로 연결됩니다.',
      ] },
    ],
  },
  company: {
    intro: '회사 기본 정보와 사규를 열람하는 화면입니다(읽기 전용).',
    sections: [
      { title: '구성', items: [
        '회사 기본 정보: 법인명, 사업자·법인등록번호, 대표자, 개업일, 소재지, 업태·종목, 세금계산서 메일.',
        '사규: 취업규칙, 휴가운영세칙, 직제규정, 직무분장세칙, 직무전결세칙, 예산·회계규정.',
        '각 사규는 장별로 펼쳐 보거나 “모두 펼치기/모두 접기”로 한 번에 볼 수 있습니다.',
      ] },
    ],
    tip: '사규 원본(PDF)이나 회사 제출용 서류 양식은 문서함에서 내려받을 수 있습니다.',
  },
  ci: {
    intro: 'DXTECH 브랜드(CI)의 의미와 사용 규정을 확인하고 로고 파일을 내려받는 화면입니다.',
    sections: [
      { title: '로고 사용', items: [
        '“로고 다운로드”에서 가로형·세로형·심볼마크·로고타입을 PNG·SVG·PSD로 내려받습니다.',
        '문서·발표 자료에는 PNG, 인쇄물·대형 출력에는 SVG를 권장합니다.',
        '로고의 모양·비율·색상을 임의로 바꾸지 말고, 주변 여백과 배경 대비를 지켜 주세요.',
      ] },
    ],
  },
  resources: {
    intro: '외부 제출용 문서 양식과 내부 문서 양식을 모아 둔 회사 공용 문서함입니다.',
    sections: [
      { title: '사용 방법', items: [
        '필요한 양식의 “다운로드”를 눌러 내려받습니다.',
        '양식 등록·삭제는 관리자가 합니다. 필요한 양식이 없으면 관리자에게 요청하세요.',
      ] },
    ],
    tip: '교육자료나 팀 공유 파일은 “자료실”에서 직접 올리고 받을 수 있습니다.',
  },
  library: {
    intro: '임직원 누구나 폴더를 만들고 파일을 올려 공유하는 공용 자료실입니다. 윈도우 탐색기처럼 사용합니다.',
    sections: [
      { title: '기본 사용', items: [
        '폴더는 더블클릭으로 열고, 파일은 더블클릭하면 내려받습니다. 위쪽 경로를 눌러 상위 폴더로 이동합니다.',
        '“새 폴더”로 폴더를 만들고, “업로드”나 끌어다 놓기로 파일을 올립니다(같은 이름이면 자동으로 이름이 바뀜).',
        '“정렬”·“보기”로 표시 방식을 바꾸고, “세부 정보”로 선택한 항목의 정보를 봅니다.',
      ] },
      { title: '이름 바꾸기·삭제', items: [
        '항목을 우클릭하거나 F2·Delete 키를 쓸 수 있습니다.',
        '이름 바꾸기와 삭제는 올린 사람(소유자)과 관리자만 할 수 있습니다.',
        '삭제한 항목은 “휴지통”으로 가며, 소유자·삭제한 사람이 복원할 수 있습니다.',
      ] },
    ],
  },
  picker: {
    intro: '참여자를 골라 사다리 게임으로 상품·상금·벌칙을 정하는 화면입니다.',
    sections: [
      { title: '진행 순서', items: [
        '참여자를 2명 이상 고릅니다(이름·부서 검색, “전체”·“해제” 가능).',
        '결과 칸마다 상품·상금·벌칙·꽝을 고르고 내용을 입력합니다. 칸 수는 참여자 수에 맞춰집니다.',
        '사다리를 만든 뒤 “▶ 한 명씩 확인” 또는 “전체 결과”로 공개합니다. “다시 섞기”로 새로 배치할 수 있습니다.',
      ] },
    ],
  },
  admin: {
    intro: '선택한 날짜에 임직원이 근무 중인지, 휴가·출장·자리비움인지와 그날의 일정을 한눈에 보는 화면입니다.',
    sections: [
      { title: '사용 방법', items: [
        '위쪽 날짜 이동으로 다른 날을 보고, 부서로 걸러 봅니다. 제목 옆에 상태별 인원이 표시됩니다.',
        '일정은 Outlook 일정에서 가져오며 2건까지 보이고 나머지는 “+N건”으로 표시됩니다.',
        '출근·퇴근 시각, 근무시간, CSV 내보내기는 대표이사·임원·관리자에게만 보입니다.',
      ] },
    ],
  },
  approve: {
    intro: '승인을 기다리는 휴가 신청을 확인하고 처리하는 화면입니다(권한이 있는 사람에게만 보임).',
    sections: [
      { title: '사용 방법', items: [
        '신청자·부서·종류·기간·일수·사유를 확인하고 “승인” 또는 “반려”(사유 입력)합니다.',
        '휴가 승인 권한은 대표이사에게 있으며, 권한이 없으면 열람만 가능합니다.',
      ] },
    ],
  },
  meals: {
    intro: '1층 로비 구내식당 메뉴를 날짜별로 보는 화면입니다(전 임직원).',
    sections: [
      { title: '사용 방법', items: [
        '“메뉴판 사진 올리기”로 로비 메뉴판을 찍어 올립니다(휴대폰에서는 바로 카메라가 열림). 같은 날짜에 다시 올리면 새 사진으로 바뀝니다.',
        '“메뉴 입력”에서 중식·석식 메뉴를 한 줄에 하나씩 적고 저장합니다. 누구나 입력·수정할 수 있고 마지막 등록자가 표시됩니다.',
        '‹ › 로 날짜를 옮겨 지난 식단을 봅니다. 사진을 누르면 크게 보입니다.',
        '사진 속 메뉴를 자동으로 읽는 기능(OCR)은 추후 지원 예정입니다.',
      ] },
    ],
  },
  kudos: {
    intro: '동료에게 칭찬·감사 카드를 보내고, 달마다 주고받은 카드와 많이 받은 사람(칭찬왕)을 보는 화면입니다(전 임직원).',
    sections: [
      { title: '사용 방법', items: [
        '“카드 보내기”에서 받는 사람·스티커·메시지(200자 이내)를 골라 보냅니다. 자신에게는 보낼 수 없습니다.',
        '카드는 모든 임직원이 볼 수 있고, 홈 화면에도 이번 달 최신 카드가 보입니다.',
        '보낸 카드는 보낸 사람(또는 관리자)만 삭제할 수 있습니다.',
      ] },
    ],
  },
  assets: {
    intro: '노트북·모니터·법인카드·소프트웨어 라이선스 같은 회사 자산의 사용자와 만료일을 관리하는 화면입니다.',
    sections: [
      { title: '사용 방법', items: [
        '관리자(대표이사·관리자)는 신규 등록·수정·삭제와 지급/회수를 하고, 지급·회수는 이력으로 남습니다.',
        '만료일이 30일 이내이거나 지난 항목은 상단에 따로 알리고, 메뉴에도 건수가 표시됩니다.',
        '그 밖의 임직원은 자신에게 지급된 장비·라이선스만 봅니다.',
        '카드번호·라이선스 키·비밀번호 같은 비밀값은 입력하지 않습니다.',
      ] },
    ],
  },
  peopleStats: {
    intro: '임직원이 사는 지역과 MBTI·혈액형 분포를 보는 화면입니다(전 임직원 열람).',
    sections: [
      { title: '구성', items: [
        '지도에는 임직원 정보의 주소에서 시/군/구만 읽어 지역마다 원으로 표시합니다. 원이 클수록 인원이 많고, 원을 누르면 이름이 보입니다.',
        '집 주소 자체는 지도에 올리지 않습니다. 주소가 없거나 읽을 수 없는 사람은 미등록으로 셉니다.',
        'MBTI·혈액형은 각자 내 정보 또는 프로파일에서 입력한 값만 집계합니다. MBTI는 E/I·S/N·T/F·J/P 비율과 유형별 인원을 함께 보여줍니다.',
      ] },
    ],
  },
  stats: {
    intro: '출근율과 휴가 사용 현황, 직원별 휴가·출퇴근 이력을 분석하는 화면입니다(권한이 있는 사람에게만 보임).',
    sections: [
      { title: '구성', items: [
        '금일 출근율·휴가 사용률·승인 대기, 부서별 출근율과 금일 상태 분포를 보여줍니다.',
        '연도와 직원을 골라 개인 휴가 이력을, 월을 골라 개인 출퇴근 이력을 봅니다.',
        '대표이사는 출퇴근 관리 예외라 집계에서 빠집니다.',
      ] },
    ],
  },
  settings: {
    intro: '임직원 명부와 근무·휴가 정책을 확인하고 공휴일을 관리하는 관리자 화면입니다.',
    sections: [
      { title: '구성', items: [
        '전체 임직원 검색, 근무 시각, 반차 시간대, 연차 정책, 휴가 결재선을 확인합니다.',
        '공휴일은 여기서 추가·삭제합니다. 그 밖의 정책 값은 확인용으로 표시됩니다.',
      ] },
    ],
  },
};
